/* eslint-disable no-undef */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import { isLatLngLiteral } from "@googlemaps/typescript-guards";

interface TrackMapProps {
  latitude: number;
  longitude: number;
  trackName: string;
  zoom?: number;
 className?: string;
}

interface MapProps {
  center: google.maps.LatLngLiteral;
  zoom: number;
  trackName: string;
}

const MapComponent = ({ center, zoom, trackName }: MapProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map>();
  const markerRef = useRef<google.maps.Marker>();

  useEffect(() => {
    if (ref.current && !mapRef.current) {
      // Initialize map
      mapRef.current = new window.google.maps.Map(ref.current, {
        center,
        zoom,
        mapTypeId: "hybrid", // Shows satellite with roads overlay - good for race tracks
        gestureHandling: "cooperative",
        zoomControl: true,
        mapTypeControl: true,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
      });
    }
  }, [center, zoom]);

  useEffect(() => {
    if (mapRef.current) {
      // Update map center and zoom
      mapRef.current.setCenter(center);
      mapRef.current.setZoom(zoom);

      // Remove existing marker
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }

      // Add new marker
      markerRef.current = new window.google.maps.Marker({
        position: center,
        map: mapRef.current,
        title: trackName,
        icon: {
          url:
            "data:image/svg+xml;charset=UTF-8," +
            encodeURIComponent(`
            <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <circle cx="16" cy="16" r="12" fill="#dc2626" stroke="#ffffff" stroke-width="2"/>
              <circle cx="16" cy="16" r="4" fill="#ffffff"/>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(32, 32),
          anchor: new window.google.maps.Point(16, 16),
        },
      });

      // Optional: Add info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; min-width: 150px;">
            <h3 style="margin: 0 0 4px 0; font-size: 14px; font-weight: bold;">${trackName}</h3>
            <p style="margin: 0; font-size: 12px; color: #666;">
              ${center.lat.toFixed(6)}, ${center.lng.toFixed(6)}
            </p>
          </div>
        `,
      });

      markerRef.current.addListener("click", () => {
        infoWindow.open(mapRef.current, markerRef.current);
      });
    }
  }, [center, trackName, zoom]);

 return <div ref={ref} className="w-full h-full" />;
};

const LoadingComponent = () => {
  const { t } = useTranslation(["pages", "common"]);
  return (
  <div className="w-full h-full flex items-center justify-center bg-secondary-100">
  <div className="text-center">
  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
  <p className="text-sm text-secondary-600">{t("pages:tracks.map.loadingMap")}</p>
      </div>
    </div>
  );
};

const ErrorComponent = ({ error }: { error: Status }) => {
  const { t } = useTranslation(["pages", "common"]);
  return (
  <div className="w-full h-full flex items-center justify-center bg-red-50 border border-red-200 rounded-lg">
  <div className="text-center p-4">
  <div className="text-red-600 mb-2">
        <svg
  className="h-8 w-8 mx-auto"
           fill="none"
           viewBox="0 0 24 24"
           stroke="currentColor"
         >
           <path
             strokeLinecap="round"
             strokeLinejoin="round"
             strokeWidth={2}
             d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
           />
         </svg>
       </div>
  <p className="text-sm text-red-800 font-medium">{t("pages:tracks.map.failedToLoadMap")}</p>
  <p className="text-xs text-red-600 mt-1">{t("pages:tracks.map.errorPrefix", { error })}</p>
  <p className="text-xs text-red-600 mt-2">
         {t("pages:tracks.map.checkApiKeyConfig")}
       </p>
     </div>
   </div>
  );
};

export default function TrackMap({
  latitude,
  longitude,
  trackName,
  zoom = 15,
  className = "w-full h-64",
}: TrackMapProps) {
  const { t } = useTranslation(["pages", "common"]);
  const center = { lat: latitude, lng: longitude };

  // Validate coordinates
  if (!isLatLngLiteral(center)) {
    return (
      <div
  className={`${className} flex items-center justify-center bg-yellow-50 border border-yellow-200 rounded-lg`}
      >
  <div className="text-center p-4">
  <p className="text-sm text-yellow-800">{t("pages:tracks.map.invalidCoordinates")}</p>
  <p className="text-xs text-yellow-600 mt-1">
            {t("pages:tracks.map.latitudeLongitude", { lat: latitude, longitude })}
           </p>
         </div>
       </div>
    );
  }

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return (
      <div
  className={`${className} flex items-center justify-center bg-primary-50 border border-primary-200 rounded-lg`}
      >
  <div className="text-center p-4">
  <div className="text-primary-600 mb-2">
             <svg
  className="h-8 w-8 mx-auto"
               fill="none"
               viewBox="0 0 24 24"
               stroke="currentColor"
             >
               <path
                 strokeLinecap="round"
                 strokeLinejoin="round"
                 strokeWidth={2}
                 d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
               />
             </svg>
           </div>
  <p className="text-sm text-primary-800 font-medium">
             {t("pages:tracks.map.apiKeyNotConfigured")}
           </p>
  <p className="text-xs text-primary-600 mt-1">
             {t("pages:tracks.map.setApiKeyEnv")}
           </p>
  <div className="mt-2 text-xs text-secondary-600 bg-secondary-100 p-2 rounded">
             <p>
               <strong>{t("pages:tracks.map.locationLabel")}</strong> {trackName}
             </p>
             <p>
               <strong>{t("pages:tracks.map.coordinatesLabel")}</strong> {latitude.toFixed(6)},{" "}
               {longitude.toFixed(6)}
             </p>
           </div>
         </div>
       </div>
    );
  }

  return (
 <div className={className}>
      <Wrapper
        apiKey={apiKey}
        render={(status: Status) => {
          switch (status) {
            case Status.LOADING:
              return <LoadingComponent />;
            case Status.FAILURE:
              return <ErrorComponent error={status} />;
            case Status.SUCCESS:
              return (
                <MapComponent
                  center={center}
                  zoom={zoom}
                  trackName={trackName}
                />
              );
          }
        }}
        libraries={["geometry"]}
      />
    </div>
  );
}
